import { useIntl } from 'react-intl'
import { useLocation, useNavigate } from 'react-router-dom'
import {
    CreatorAccommodationStep,
    GlobalStep,
    Step,
    UnlimitedCardStep,
    UnlimitedRenewStep,
    VouchersStep,
} from './general-slice/types'
import {
    GlobalStepObject,
    GLOBAL_STEPS,
    CreatorStepObject,
    STEPS,
    CreatorAccommodationStepObject,
    CREATOR_ACCOMMODATION_STEPS,
    SelfcareStepObject,
    SELFCARE_STEPS,
    UnlimitedCardStepObject,
    UNLIMITED_CARD_STEPS,
    UnlimitedRenewStepObject,
    UNLIMITED_RENEW_STEPS,
    VouchersStepObject,
    VOUCHERS_STEPS,
} from 'routes/steps'
import { SelfcareStep } from 'store/general-slice/types'
import { scrollToTop } from 'helpers/animateScroll'
import { useCart } from './cart-slice/hooks'
import { useAppDispatch, useAppSelector } from './hooks'
import { setNextStepToEdit } from './general-slice'
import { removeTrailingSlashFromPathName } from 'helpers/helpers'
import { firstStep } from 'routes/basenames'
import { getLanguageFromLocale } from 'helpers/i18n'
import { Languages } from 'helpers/enum'

export type LanguageType = `${Languages}`

export const useApp = () => {
    const { locale } = useIntl()
    const { pathname } = useLocation()
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const { isStepEmpty } = useCart()
    const { appMode } = useAppSelector((state) => state.general)

    const isCreatorAccommodation = appMode === 'creator-accommodation'
    const isCreatorMode = appMode === 'creator' || isCreatorAccommodation
    const isUnlimitedRenewMode = appMode === 'unlimited-renew'
    const isUnlimitedMode = appMode === 'unlimited-card' || isUnlimitedRenewMode

    const language: LanguageType = getLanguageFromLocale(locale)

    let localSteps: (
        | CreatorStepObject
        | CreatorAccommodationStepObject
        | SelfcareStepObject
        | UnlimitedCardStepObject
        | UnlimitedRenewStepObject
        | VouchersStepObject
    )[] = STEPS

    switch (appMode) {
        case 'selfcare':
            localSteps = SELFCARE_STEPS
            break
        case 'unlimited-card':
            localSteps = UNLIMITED_CARD_STEPS
            break
        case 'unlimited-renew':
            localSteps = UNLIMITED_RENEW_STEPS
            break
        case 'creator-accommodation':
            localSteps = CREATOR_ACCOMMODATION_STEPS
            break
        case 'vouchers':
            localSteps = VOUCHERS_STEPS
            break
        default:
            localSteps = STEPS
    }

    const globalSteps: GlobalStepObject[] =
        appMode === 'global' ? GLOBAL_STEPS : []

    const step =
        localSteps.find(
            (step) =>
                step.slug[language] ===
                removeTrailingSlashFromPathName(pathname)
        ) ||
        globalSteps.find(
            (step) =>
                step.slug[language] ===
                removeTrailingSlashFromPathName(pathname)
        )

    const stepId = step?.id
    const stepPath = step?.slug[language]

    const getPath = (
        stepName:
            | Step
            | CreatorAccommodationStep
            | SelfcareStep
            | GlobalStep
            | UnlimitedCardStep
            | UnlimitedRenewStep
            | VouchersStep
    ) =>
        localSteps.find((step) => step.id === stepName)?.slug[language] ||
        globalSteps.find((step) => step.id === stepName)?.slug[language] ||
        firstStep

    const stepIndex =
        localSteps.findIndex(
            (step) =>
                step.slug[language] ===
                removeTrailingSlashFromPathName(pathname)
        ) || 0

    const navigateToStep = ({
        withoutScrollTop,
        withoutScrollAnimation,
        stepId,
    }: {
        stepId:
            | Step
            | CreatorAccommodationStep
            | SelfcareStep
            | UnlimitedCardStep
            | UnlimitedRenewStep
            | VouchersStep
        withoutScrollAnimation?: boolean
        withoutScrollTop?: boolean
    }) => {
        const index = localSteps.findIndex((step) => step.id === stepId) || 0
        navigate(localSteps[index].slug[language])

        if (!withoutScrollTop) {
            const scrollDuration = withoutScrollAnimation ? 0 : 1000
            scrollToTop(scrollDuration)
        }
    }

    const goBackToStep = (
        stepToEdit:
            | Step
            | CreatorAccommodationStep
            | SelfcareStep
            | UnlimitedCardStep
            | UnlimitedRenewStep
            | VouchersStep
    ) => {
        let areAllNextStepsEmpty = true

        switch (stepToEdit) {
            case 'tickets':
                areAllNextStepsEmpty =
                    appMode === 'creator'
                        ? isStepEmpty('upsell') && isStepEmpty('accommodation')
                        : isStepEmpty('upsell')
                break
            case 'upsell':
                areAllNextStepsEmpty =
                    appMode !== 'creator' || isStepEmpty('accommodation')
                break
            case 'accommodation':
                areAllNextStepsEmpty =
                    appMode === 'creator' ||
                    (isStepEmpty('tickets') && isStepEmpty('upsell'))
                break
        }

        if (areAllNextStepsEmpty) {
            navigate(getPath(stepToEdit))
        } else {
            dispatch(setNextStepToEdit(stepToEdit))
        }
    }

    return {
        language,
        stepId,
        stepPath,
        stepIndex,
        appMode,
        isCreatorMode,
        isCreatorAccommodation,
        isUnlimitedMode,
        navigateToStep,
        goBackToStep,
        getPath,
    }
}
